import { Component, inject, input, output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { SvgIconComponent } from 'angular-svg-icon';
import { SkeletonModule } from 'primeng/skeleton';
import { CardComponent } from '@/shared/ui-components/card/card.component';
import { ButtonComponent } from '@/shared/ui-components/button/button.component';
import { TagComponent } from '@/shared/ui-components/tag/tag.component';
import { RmOpenActionDto } from '../../data-access/relationship-manager-dashboard.model';
import {
    NEW_TASK_STATUS,
    NewTaskPriority,
    NewTaskPriorityValue,
    NewTaskStatus,
} from '@/pages/new-tasks/data-access/new-tasks.model';

@Component({
    selector: 'app-rm-open-actions',
    standalone: true,
    imports: [
        CommonModule,
        TranslateModule,
        SvgIconComponent,
        SkeletonModule,
        CardComponent,
        ButtonComponent,
        TagComponent,
    ],
    templateUrl: './rm-open-actions.component.html',
})
export class RmOpenActionsComponent {
    private readonly _router = inject(Router);

    public readonly items = input<RmOpenActionDto[]>([]);
    public readonly totalCount = input(0);
    public readonly loading = input(false);
    public readonly viewAllLink = input<string | readonly unknown[]>('/rm/new-tasks/deals/deal-approval');
    public readonly viewAllClick = output<void>();

    public onViewAll(): void {
        this.viewAllClick.emit();
        const link = this.viewAllLink();
        if (typeof link === 'string') {
            void this._router.navigateByUrl(link);
        } else if (link) {
            void this._router.navigate(link as unknown[]);
        }
    }

    public showViewAll(): boolean {
        return this.totalCount() > 0;
    }

    public onActionClick(item: RmOpenActionDto): void {
        if (item.dealId != null) {
            void this._router.navigate(['/rm', 'deal-details', item.dealId]);
            return;
        }
        if (item.clientId != null) {
            void this._router.navigate(['/rm/clients'], { queryParams: { clientId: item.clientId } });
        }
    }

    public hasActionLink(item: RmOpenActionDto): boolean {
        return item.dealId != null || item.clientId != null;
    }

    public getTaskIconPath(_priority: NewTaskPriorityValue): string {
        return '/icons/alert.svg';
    }

    public priorityLabel(priority: NewTaskPriorityValue): string {
        switch (priority) {
            case NewTaskPriority.LOW:
                return 'Low';
            case NewTaskPriority.MEDIUM:
                return 'Medium';
            case NewTaskPriority.HIGH:
                return 'High';
            case NewTaskPriority.CRITICAL:
                return 'Critical';
            default:
                return '-';
        }
    }

    public statusLabel(status: NewTaskStatus): string {
        switch (status) {
            case NEW_TASK_STATUS.OPEN:
                return 'Pending';
            case NEW_TASK_STATUS.IN_PROGRESS:
                return 'In Progress';
            case NEW_TASK_STATUS.APPROVED:
                return 'Approved';
            case NEW_TASK_STATUS.REJECTED:
                return 'Rejected';
            case NEW_TASK_STATUS.CANCELLED:
                return 'Cancelled';
            case NEW_TASK_STATUS.COMPLETED:
                return 'Completed';
            default:
                return '-';
        }
    }

    public prioritySeverity(priority: NewTaskPriorityValue): 'danger' | 'warn' | 'success' | 'secondary' {
        switch (priority) {
            case NewTaskPriority.HIGH:
            case NewTaskPriority.CRITICAL:
                return 'danger';
            case NewTaskPriority.MEDIUM:
                return 'warn';
            case NewTaskPriority.LOW:
                return 'success';
            default:
                return 'secondary';
        }
    }

    public statusSeverity(status: NewTaskStatus): 'warn' | 'info' | 'success' | 'danger' | 'secondary' {
        switch (status) {
            case NEW_TASK_STATUS.OPEN:
                return 'warn';
            case NEW_TASK_STATUS.IN_PROGRESS:
                return 'info';
            case NEW_TASK_STATUS.APPROVED:
            case NEW_TASK_STATUS.COMPLETED:
                return 'success';
            case NEW_TASK_STATUS.REJECTED:
                return 'danger';
            case NEW_TASK_STATUS.CANCELLED:
                return 'secondary';
            default:
                return 'secondary';
        }
    }
}
